/**
 * Deterministic ticket-type ordering + category-mapping resolution.
 *
 * Event.categoryMapping historically stored ONLY ticketTypeIndex — a position
 * into "ticket types ordered by sortOrder". When sortOrders tie (the edit form
 * sends 1 for every ticket type), Postgres returns ties in arbitrary order, so
 * the index resolved to a DIFFERENT ticket type per query — admin mapped $30,
 * storefront charged/displayed some other ticket's price, nondeterministically.
 *
 * Fixes, in order of authority:
 *  1. Mapping entries now also carry ticketTypeId (written at save time).
 *     Resolvers MUST prefer it.
 *  2. Legacy index-only entries resolve against a list ordered by
 *     (sortOrder asc, id asc) — same tiebreak everywhere, DB and JS.
 */

export const TICKET_TYPE_ORDER_BY = [
  { sortOrder: 'asc' as const },
  { id: 'asc' as const },
]

export interface CategoryMappingEntry {
  categoryId: string
  ticketTypeId?: string
  ticketTypeIndex?: number
}

export function sortTicketTypesDeterministic<T extends { id: string; sortOrder?: number | null }>(
  ticketTypes: T[]
): T[] {
  return [...ticketTypes].sort(
    (a, b) =>
      ((a.sortOrder ?? 0) - (b.sortOrder ?? 0)) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  )
}

/**
 * Resolve a mapping entry to a ticket type. `orderedTicketTypes` must be the
 * FULL list (including add-ons and hidden types — legacy indexes were built
 * against the admin form's full array) ordered by (sortOrder, id). Callers
 * decide what to do with add-on results (usually: refuse as a price source).
 */
export function resolveMappedTicketType<T extends { id: string }>(
  entry: CategoryMappingEntry,
  orderedTicketTypes: T[]
): T | null {
  if (entry.ticketTypeId) {
    const byId = orderedTicketTypes.find(t => t.id === entry.ticketTypeId)
    if (byId) return byId
  }
  if (entry.ticketTypeIndex != null && entry.ticketTypeIndex >= 0) {
    return orderedTicketTypes[entry.ticketTypeIndex] ?? null
  }
  return null
}

/**
 * Collapse a categoryMapping — which may hold MULTIPLE rows per category
 * (Adult + Child, or a box-office Comp added alongside the public type) —
 * to ONE baked-price source per category. Preference order: first mapping
 * row whose ticket type is public (not an add-on, not box-office-only),
 * falling back to the first non-addon row. Every seat-price bake MUST go
 * through this: iterating raw mapping rows lets the LAST row win the
 * UPDATE, which is how appending a $0 box-office Comp to each category
 * silently zeroed every FREE seat's storefront price.
 */
export function resolveCategoryPriceSources<
  T extends { id: string; isAddon?: boolean; channelRestrict?: string | null }
>(mapping: CategoryMappingEntry[], orderedTicketTypes: T[]): Map<string, T> {
  const byCategory = new Map<string, T[]>()
  for (const m of mapping) {
    if (!m.categoryId) continue
    const tt = resolveMappedTicketType(m, orderedTicketTypes)
    if (!tt || tt.isAddon) continue
    const list = byCategory.get(m.categoryId) || []
    list.push(tt)
    byCategory.set(m.categoryId, list)
  }
  const out = new Map<string, T>()
  for (const [catId, candidates] of byCategory) {
    out.set(catId, candidates.find(t => t.channelRestrict !== 'boxoffice') ?? candidates[0])
  }
  return out
}
